import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados das alçadas de aprovação por faixa de valor.
 *
 * Irmã de `parametros/alcadasGenericas/dados.ts` (o motor de alçadas): lá ficam
 * as regras genéricas avaliadas pelo motor, aqui as faixas de valor que decidem
 * quem aprova o quê. Mesmo desenho: nenhum componente chama `chamarApi` direto,
 * chave de cache junto de quem a usa, `retry: naoInsistirEmRecusa` em toda
 * consulta (CLAUDE.md, "Regras de API").
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * `/alcadas/valor` ainda não existe na API. A tabela `alcadas_aprovacao_valor`
 * existe no banco desde a migração `0002_dominio_base.sql`, mas nenhuma rota a
 * expõe. Decisão do dono de 2026-08-31 (CLAUDE.md, "Proibido", exceção estendida
 * às telas de configuração e parametrização) — mesmas três condições de sempre:
 * só leitura, dado claramente fictício, removido antes de `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`).
 * "Salvar" continua sem gravar nada — ver `AlcadasValorPage.tsx`.
 */

/** O assunto sobre o qual a alçada decide. `contexto` é `text` no banco. */
export const CONTEXTOS_DE_ALCADA = [
  { valor: 'MULTA', rotulo: 'Multas' },
  { valor: 'COBRANCA', rotulo: 'Cobranças' },
  { valor: 'MEDICAO', rotulo: 'Medições' },
  { valor: 'DOCUMENTO_LIQUIDACAO', rotulo: 'Documentos de liquidação' },
] as const;

export type ContextoDeAlcada = (typeof CONTEXTOS_DE_ALCADA)[number]['valor'];

/**
 * Os papéis que podem figurar como aprovador.
 *
 * Subconjunto de `PAPEIS` (`apps/api/src/nucleo/papeis.ts`): só os internos da
 * DTP aprovam por alçada de valor. Papel de rede de ensino e papel externo não
 * entram — quem aprova gasto é a Secretaria dona do contrato.
 */
export const PERFIS_APROVADORES = [
  { valor: 'GESTOR', rotulo: 'Gestor' },
  { valor: 'GESTOR_ADMIN', rotulo: 'Gestor administrativo' },
  { valor: 'ADMIN', rotulo: 'Administrador' },
  { valor: 'FINANCEIRO', rotulo: 'Financeiro' },
  { valor: 'AUDITOR', rotulo: 'Auditor' },
] as const;

/**
 * Uma faixa de alçada, como a API (ainda inexistente) devolveria.
 *
 * `valor_minimo`/`valor_maximo` seguem a convenção do banco para dinheiro —
 * `numeric(14,2)` entregue como string decimal em reais (CLAUDE.md, "Regras de
 * banco", item 7). `valor_maximo` nulo significa "sem teto".
 */
export interface AlcadaDeValor {
  id: string;
  contexto: ContextoDeAlcada;
  nome: string;
  descricao: string | null;
  valor_minimo: number | string;
  valor_maximo: number | string | null;
  perfil_aprovador: string;
  exige_dupla_aprovacao: boolean;
  perfil_segundo_aprovador: string | null;
  ativo: boolean;
  ordem: number;
}

export const CHAVES_DE_ALCADAS_DE_VALOR = {
  porContexto: (contexto: ContextoDeAlcada) => ['alcadas-valor', contexto] as const,
};

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Faixas de exemplo por contexto. As de MULTA têm uma lacuna proposital entre
// 5.000,01 e 10.000,00 para a régua visual ter o que denunciar — é justamente
// o defeito de parametrização que a tela existe para tornar visível.
const ALCADAS_MOCK: Record<ContextoDeAlcada, AlcadaDeValor[]> = {
  MULTA: [
    {
      id: 'alcada-exemplo-multa-01',
      contexto: 'MULTA',
      nome: 'Multa de baixo valor',
      descricao: 'Aplicação direta pelo fiscal responsável pela ocorrência.',
      valor_minimo: '0.00',
      valor_maximo: '5000.00',
      perfil_aprovador: 'GESTOR',
      exige_dupla_aprovacao: false,
      perfil_segundo_aprovador: null,
      ativo: true,
      ordem: 1,
    },
    {
      id: 'alcada-exemplo-multa-02',
      contexto: 'MULTA',
      nome: 'Multa de alto valor',
      descricao: 'Exige segunda assinatura da gestão administrativa.',
      valor_minimo: '10000.01',
      valor_maximo: null,
      perfil_aprovador: 'GESTOR_ADMIN',
      exige_dupla_aprovacao: true,
      perfil_segundo_aprovador: 'ADMIN',
      ativo: true,
      ordem: 2,
    },
  ],
  COBRANCA: [
    {
      id: 'alcada-exemplo-cobranca-01',
      contexto: 'COBRANCA',
      nome: 'Cobrança ordinária',
      descricao: null,
      valor_minimo: '0.00',
      valor_maximo: '20000.00',
      perfil_aprovador: 'FINANCEIRO',
      exige_dupla_aprovacao: false,
      perfil_segundo_aprovador: null,
      ativo: true,
      ordem: 1,
    },
    {
      id: 'alcada-exemplo-cobranca-02',
      contexto: 'COBRANCA',
      nome: 'Cobrança de valor relevante',
      descricao: 'Acima do teto ordinário, passa pela gestão administrativa.',
      valor_minimo: '20000.01',
      valor_maximo: null,
      perfil_aprovador: 'GESTOR_ADMIN',
      exige_dupla_aprovacao: true,
      perfil_segundo_aprovador: 'FINANCEIRO',
      ativo: true,
      ordem: 2,
    },
  ],
  MEDICAO: [
    {
      id: 'alcada-exemplo-medicao-01',
      contexto: 'MEDICAO',
      nome: 'Medição mensal ordinária',
      descricao: 'Ateste em dupla alçada, como manda o fluxo de apuração.',
      valor_minimo: '0.00',
      valor_maximo: null,
      perfil_aprovador: 'GESTOR',
      exige_dupla_aprovacao: true,
      perfil_segundo_aprovador: 'GESTOR_ADMIN',
      ativo: true,
      ordem: 1,
    },
  ],
  DOCUMENTO_LIQUIDACAO: [
    {
      id: 'alcada-exemplo-liquidacao-01',
      contexto: 'DOCUMENTO_LIQUIDACAO',
      nome: 'Liquidação até o teto do financeiro',
      descricao: null,
      valor_minimo: '0.00',
      valor_maximo: '50000.00',
      perfil_aprovador: 'FINANCEIRO',
      exige_dupla_aprovacao: false,
      perfil_segundo_aprovador: null,
      ativo: true,
      ordem: 1,
    },
    {
      id: 'alcada-exemplo-liquidacao-02',
      contexto: 'DOCUMENTO_LIQUIDACAO',
      nome: 'Liquidação acima do teto',
      descricao: 'Auditoria acompanha a segunda assinatura.',
      valor_minimo: '50000.01',
      valor_maximo: null,
      perfil_aprovador: 'ADMIN',
      exige_dupla_aprovacao: true,
      perfil_segundo_aprovador: 'AUDITOR',
      ativo: false,
      ordem: 2,
    },
  ],
};

/**
 * As faixas de alçada de um contexto, na ordem em que são avaliadas.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useAlcadasDeValor(
  contexto: ContextoDeAlcada,
): UseQueryResult<AlcadaDeValor[], ErroApi> {
  return useQuery<AlcadaDeValor[], ErroApi>({
    queryKey: CHAVES_DE_ALCADAS_DE_VALOR.porContexto(contexto),
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => ALCADAS_MOCK[contexto] ?? [],
    retry: naoInsistirEmRecusa,
  });
}

/** O rótulo em português de um papel aprovador, com recuo para o próprio valor. */
export function rotuloDoPerfil(valor: string | null): string {
  if (!valor) {
    return '—';
  }
  return PERFIS_APROVADORES.find((perfil) => perfil.valor === valor)?.rotulo ?? valor;
}

/** Uma faixa com os limites já numéricos, pronta para comparação. */
export interface FaixaNormalizada {
  alcada: AlcadaDeValor;
  minimo: number;
  /** `Infinity` quando não há teto. */
  maximo: number;
}

/** Normaliza e ordena as faixas por valor mínimo, para a régua e o diagnóstico. */
export function normalizarFaixas(alcadas: AlcadaDeValor[]): FaixaNormalizada[] {
  return alcadas
    .map((alcada) => ({
      alcada,
      minimo: Number(alcada.valor_minimo),
      maximo: alcada.valor_maximo == null ? Infinity : Number(alcada.valor_maximo),
    }))
    .sort((a, b) => a.minimo - b.minimo);
}

/** Um defeito de parametrização entre faixas vizinhas. */
export interface DefeitoDeFaixa {
  tipo: 'lacuna' | 'sobreposicao';
  de: number;
  ate: number;
}

/**
 * Lacunas e sobreposições entre as faixas ativas.
 *
 * Lacuna é valor que ninguém aprova; sobreposição é valor que duas faixas
 * disputam. As duas são erro de parametrização, e é para isso que a régua serve.
 * Só faixas ativas entram — uma faixa inativa não cria buraco, ela já não vale.
 */
export function encontrarDefeitos(faixas: FaixaNormalizada[]): DefeitoDeFaixa[] {
  const ativas = faixas.filter((faixa) => faixa.alcada.ativo);
  const defeitos: DefeitoDeFaixa[] = [];

  for (let i = 0; i < ativas.length - 1; i += 1) {
    const atual = ativas[i];
    const proxima = ativas[i + 1];
    if (!atual || !proxima) continue;

    if (proxima.minimo > atual.maximo) {
      defeitos.push({ tipo: 'lacuna', de: atual.maximo, ate: proxima.minimo });
    } else if (proxima.minimo <= atual.maximo && atual.maximo !== Infinity) {
      defeitos.push({ tipo: 'sobreposicao', de: proxima.minimo, ate: atual.maximo });
    }
  }

  return defeitos;
}

/** A faixa que decidiria um valor, ou `null` quando ele cai numa lacuna. */
export function faixaQueDecide(faixas: FaixaNormalizada[], valor: number): FaixaNormalizada | null {
  return (
    faixas.find((faixa) => faixa.alcada.ativo && valor >= faixa.minimo && valor <= faixa.maximo) ??
    null
  );
}
